"use client";

import { cn, transformIpfs } from "utils";
import { useEffect, useState } from "react";

import { AppImage } from "@/src/components/NextImage";

export interface AvatarWithFallbackProps {
  /** 头像图片URL */
  src?: string;
  /** 图片alt文本，用于无障碍访问 */
  alt: string;
  /** 头像宽度，默认36px */
  width?: number;
  /** 头像高度，默认36px */
  height?: number;
  /** 自定义CSS类名 */
  className?: string;
  /** 默认头像路径，当src加载失败时使用 */
  fallbackSrc?: string;
  /** 头像形状，默认圆形 */
  shape?: "circle" | "square" | "rounded";
  /** 是否显示加载状态 */
  showLoading?: boolean;
  /** 加载失败时的回调函数 */
  onError?: (error: Error) => void;
  /** 加载成功时的回调函数 */
  onLoad?: () => void;
}

/**
 * AvatarWithFallback 组件
 *
 * 一个带有错误处理和默认头像回退功能的头像组件。
 * 当头像加载失败时，自动回退到默认头像，确保用户体验的连续性。
 *
 * @example
 * ```tsx
 * // 基础用法
 * <AvatarWithFallback
 *   src="https://example.com/avatar.jpg"
 *   alt="用户头像"
 * />
 *
 * // 自定义尺寸和样式
 * <AvatarWithFallback
 *   src="https://example.com/avatar.jpg"
 *   alt="用户头像"
 *   width={48}
 *   height={48}
 *   className="border-2 border-blue-500"
 *   shape="rounded"
 * />
 *
 * // 自定义默认头像
 * <AvatarWithFallback
 *   src="https://example.com/avatar.jpg"
 *   alt="用户头像"
 *   fallbackSrc="/custom-default-avatar.png"
 * />
 * ```
 */
export const AvatarWithFallback = ({
  src,
  alt,
  width = 36,
  height = 36,
  className,
  fallbackSrc = "/assets/images/ic_no_profile_avatar.svg",
  shape = "circle",
  showLoading = false,
  onError,
  onLoad,
}: AvatarWithFallbackProps) => {
  const [imgSrc, setImgSrc] = useState<string | undefined>(src);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 当src变化时重置状态
  useEffect(() => {
    setImgSrc(src);
    setHasError(false);
    setIsLoading(true);
  }, [src]);

  const handleError = (error: any) => {
    setHasError(true);
    setIsLoading(false);
    setImgSrc(transformIpfs(fallbackSrc));

    // 调用错误回调
    if (onError) {
      onError(new Error(`Failed to load avatar: ${error}`));
    }
  };

  const handleLoad = () => {
    setHasError(false);
    setIsLoading(false);

    // 调用加载成功回调
    if (onLoad) {
      onLoad();
    }
  };

  // 如果没有src或发生错误，使用默认头像
  const finalSrc =
    imgSrc && !hasError ? transformIpfs(imgSrc) : transformIpfs(fallbackSrc);

  // 根据形状确定样式类
  const shapeClasses = {
    circle: "rounded-full",
    square: "rounded-none",
    rounded: "rounded-lg",
  };

  const imageClassName = cn(
    "transition-all duration-200",
    shapeClasses[shape],
    className,
  );

  return (
    <div className="relative">
      <AppImage
        className={imageClassName}
        src={finalSrc}
        alt={alt}
        width={width}
        height={height}
        onError={handleError}
        onLoad={handleLoad}
      />

      {/* 加载状态指示器 */}
      {showLoading && isLoading && (
        <div
          role="status"
          className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-full"
        >
          <div className="w-4 h-4 border-2 border-border_white_solid border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default AvatarWithFallback;
